import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { PATHS } from "@router/paths";
import { getTKeyGirlCategory } from "@constants/girlCategories";
import { CharacterTypes, Characters } from "@services/ayolApi/api.dtos";
import { GirlProfleProvider, useGirlProfle } from "@contexts/GirlProfile.context";

import MainLayout from "@components/layouts/MainLayout/MainLayout";
import LoadingScreen from "@app/LoadingScreen/LoadingScreen";

import Stack from "@components/arrangement/Stack/Stack";
import ImageSlider from "@components/common/ImageSlider/ImageSlider";
import Button from "@components/toolkit/buttons/Button/Button";
import CharacterSticker from "@components/common/CharacterSticker/CharacterSticker";
import CharacterIcon from "@components/common/CharacterIcon/CharacterIcon";

import { ReactComponent as ChatIcon } from "@images/icons/icon-chat.svg";
import { ReactComponent as ImageIcon } from "@images/icons/icon-image.svg";

import "./GirlProfile.scss";

const GirlProfile = () => {
  const navigate = useNavigate();
  const { girlId } = useParams();
  const { girl, status, fetchGirlProfile } = useGirlProfle();

  const { t } = useTranslation(["apiResponse", "common"]);

  const loading = status === "loading";
  const failedGetProfile = status === "failed";

  const categoryName = t(getTKeyGirlCategory(girl?.categories[0].category || "") as any);
  const dominanteCharacter = (girl?.characters[0].key as CharacterTypes) || Characters.FIERY;
  const images = [...(girl?.images || []), ...(girl?.blurredPhotos || [])];

  const handleChatNow = () => {
    const chatPath = `${PATHS.chats.path}?girl=${girl?.id}`;
    navigate(chatPath, { replace: true });
  };

  const handleRefetchGirlProfile = () => {
    if (!girlId) return;

    fetchGirlProfile(girlId);
  };

  if (loading) return <LoadingScreen />;

  if (failedGetProfile) {
    return (
      <MainLayout>
        <Stack fullSize alignItems="center" justifyContent="center">
          <img src="/images/icons/icon-error.svg" alt="Error icon" />

          <Stack gap={12} alignItems="center" className="mt-8">
            <h4>{t("apiResponse:error.baseSmall")}</h4>
            <p className="p1">{t("apiResponse:error.getGirlProfile")}</p>
          </Stack>

          <Button className="mt-6" onClick={handleRefetchGirlProfile}>
            {t("common:refetch")}
          </Button>
        </Stack>
      </MainLayout>
    );
  }

  return (
    <MainLayout withGoBack>
      <div className="girl-profile">
        <div className="profile-images">
          {girl?.images && <ImageSlider images={images} />}

          <div className="character-type-icon">
            <CharacterIcon character={dominanteCharacter} />
          </div>
        </div>

        <div className="profile-info">
          <div className="profile-info-content">
            <p className="p2 girl-category">{categoryName}</p>
            <h1 className="h2 girl-name">{girl?.name}</h1>

            <p className="p0 girl-description">{girl?.description}</p>

            <div className="girl-personality">
              <h4>{t("common:personality")}</h4>

              <div className="girl-personality-stickers">
                {girl?.characters.map(({ key, value }) => {
                  if (value <= 0) return <></>;
                  else
                    return (
                      <CharacterSticker key={`${girl.id}-${key}`} character={key as CharacterTypes} rate={value} />
                    );
                })}
              </div>
            </div>

            <hr className="profile-info-breakLine no-select" />

            <div className="profile-info-buttons">
              <Button fullWidth onClick={handleChatNow}>
                <ChatIcon /> {t("common:chatNow")}
              </Button>

              <Button variant="secondary" fullWidth comingSoon>
                <ImageIcon /> {t("common:generateImages")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default (
  <GirlProfleProvider>
    <GirlProfile />
  </GirlProfleProvider>
);
